/**
 * #2448 — the wizard's hospital question, with the pre-screen's typeahead.
 *
 * The pre-screen (`ReferralBlock`) suggests partner hospitals as the family
 * types; the full application then asked the same question as a bare text
 * box. This offers the same suggestions over the same public search
 * (`GET /api/partner-hospitals?q=`) while keeping the answer free text: the
 * question is "Hospital(s)", so a child treated in two places writes both,
 * comma-separated, and only the part after the last comma is searched and
 * replaced by a pick. A hospital that is not a partner is simply typed.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Input } from '@/components/ui/input';
import { api, type PartnerHospitalSummary } from '@/services/api';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

/** The text before the segment being typed, and that segment. */
function splitLast(text: string): { head: string; term: string } {
  const at = text.lastIndexOf(',');
  return at < 0 ? { head: '', term: text.trim() } : { head: text.slice(0, at + 1), term: text.slice(at + 1).trim() };
}

interface Props {
  id: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  ariaRequired?: boolean;
}

export function HospitalSuggestInput({ id, value, onChange, onBlur, placeholder, ariaRequired }: Props) {
  const [results, setResults] = useState<PartnerHospitalSummary[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  // Typing opens the search; a pick (or the value arriving pre-filled) does not.
  const typedRef = useRef(false);
  const listId = `${id}-suggestions`;

  const { term } = splitLast(value);
  useEffect(() => {
    if (!typedRef.current || term.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setOpen(false);
      return undefined;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      api.partnerHospitals.search(term)
        .then((res) => {
          if (cancelled) return;
          const hits = (res.data ?? []).filter((h) => h.name.toLowerCase() !== term.toLowerCase());
          setResults(hits);
          setOpen(hits.length > 0);
          setActive(0);
        })
        .catch(() => { if (!cancelled) { setResults([]); setOpen(false); } });
    }, DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [term]);

  const pick = (hit: PartnerHospitalSummary) => {
    typedRef.current = false;
    const { head } = splitLast(value);
    onChange(head ? `${head} ${hit.name}` : hit.name);
    setOpen(false);
    setResults([]);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (i + 1) % results.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (i - 1 + results.length) % results.length); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(results[active]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div className="relative">
      <Input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open ? `${listId}-${active}` : undefined}
        autoComplete="off"
        value={value}
        onChange={(e) => { typedRef.current = true; onChange(e.target.value); }}
        onKeyDown={onKeyDown}
        onBlur={() => { setOpen(false); onBlur?.(); }}
        placeholder={placeholder}
        aria-required={ariaRequired}
      />
      {open && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-border bg-background py-1 shadow-md"
        >
          {results.map((hit, i) => (
            <li
              key={hit.id}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              // mousedown, not click: the input's blur closes the list first.
              onMouseDown={(e) => { e.preventDefault(); pick(hit); }}
              onMouseEnter={() => setActive(i)}
              className={`cursor-pointer px-3 py-2 text-body ${i === active ? 'bg-muted' : ''}`}
            >
              <span className="text-foreground">{hit.name}</span>
              {(hit.city || hit.state) && (
                <span className="ms-2 text-caption text-stevens-ink-3">{[hit.city, hit.state].filter(Boolean).join(', ')}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
